import type { SimulatorField } from '@vedalib/editor/lib/elements'
import { useCallback, useState } from 'react'

import { useLiveFeedback } from '../../courseKit/CourseKitUtils'

interface IUseFieldValidationProps {
  fields: SimulatorField[] | undefined
  elementId: string
}

export const useFieldValidation = ({ fields, elementId }: IUseFieldValidationProps) => {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [invalidFields, setInvalidFields] = useState<{ id: string; message: string }[]>([])
  const announce = useLiveFeedback('polite')

  const onFieldValueChange = useCallback((fieldId: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }))
    setInvalidFields((prev) => prev.filter((field) => field.id !== fieldId))
  }, [])

  const validateFieldOnBlur = useCallback(
    (fieldId: string) => {
      const field = fields?.find((f) => f.id === fieldId)
      if (!field || field.withoutValidation) {
        return
      }

      const enteredValue = fieldValues[fieldId] || ''

      if (!enteredValue.trim()) {
        return
      }

      const nonEmptyValues = field.validationValues?.filter((v) => v.trim()) || []

      if (field.validationOnValue && nonEmptyValues.length > 0) {
        const isValid = nonEmptyValues.some(
          (v) => v.toLowerCase().trim() === enteredValue.toLowerCase().trim(),
        )
        if (!isValid) {
          setInvalidFields((prev) =>
            prev.some((field) => field.id === fieldId)
              ? prev
              : [...prev, { id: fieldId, message: 'Incorrect answer' }],
          )
        } else {
          setInvalidFields((prev) => prev.filter((field) => field.id !== fieldId))
        }
      } else if (field.validationOnValue) {
        setInvalidFields((prev) => prev.filter((field) => field.id !== fieldId))
      }
    },
    [fields, fieldValues],
  )

  const validateFields = useCallback(() => {
    const currentFields = fields || []
    const invalidFields: { id: string; message: string }[] = []

    for (const field of currentFields) {
      if (field.withoutValidation) {
        continue
      }

      const enteredValue = fieldValues[field.id] || ''

      const nonEmptyValues = field.validationValues?.filter((v) => v.trim()) || []

      if (field.validationOnValue && nonEmptyValues.length > 0) {
        const isValid = nonEmptyValues.some(
          (v) => v.toLowerCase().trim() === enteredValue.toLowerCase().trim(),
        )
        if (!isValid) {
          invalidFields.push({ id: field.id, message: 'Error: Incorrect answer' })
        }
      } else if (field.validationOnValue) {
        if (!enteredValue.trim()) {
          invalidFields.push({ id: field.id, message: 'Error: This field is required' })
        }
      } else if (field.value) {
        if (!enteredValue.trim()) {
          invalidFields.push({ id: field.id, message: 'Error: This field is required' })
        }
      }
    }

    announce(
      invalidFields[0]?.message ? 'Please fix errors before continuing' : 'All answers are correct',
    )
    const firstInvalidFieldNode = document.getElementById(`${elementId}-${invalidFields[0]?.id}`)
    if (firstInvalidFieldNode) {
      firstInvalidFieldNode.focus()
    }
    setInvalidFields(invalidFields)

    return invalidFields.length === 0
  }, [fields, fieldValues, announce, elementId])

  return {
    fieldValues,
    invalidFields,
    onFieldValueChange,
    validateFieldOnBlur,
    validateFields,
  }
}
